import { useState } from "react";
import {
  makeDeleteApiCall,
  makePostApiCall,
} from "../../../shared/makeApiCall";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../contexts/ToastContext";

export function TenantsViewModel() {
  const { showSuccess, showError } = useToast();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ first: 0, page: 1, rows: 15, total: 0 });
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetchTenants = async (page = 1, searchTerm = "") => {
    setLoading(true);
    const payload = { page, search: searchTerm };
    const res = await makePostApiCall("/admin/tenants/search", payload);
    if (res.success) {
      setTenants(res.data.data);
      setPagination({
        first: (res.data.current_page - 1) * res.data.per_page,
        page: res.data.current_page,
        rows: res.data.per_page,
        total: res.data.total,
      });
    } else {
      showError(res.data?.message || "Unable to load tenants.");
    }
    setLoading(false);
  };

  const deleteTenant = async (tenantId) => {
    if (!window.confirm("Delete this tenant and all of its users?")) {
      return;
    }

    setLoading(true);
    const res = await makeDeleteApiCall(`/admin/tenants/${tenantId}`);
    if (res.success) {
      setTenants((current) => current.filter((tenant) => tenant.id !== tenantId));
      showSuccess("Tenant deleted successfully.");
    } else {
      showError(res.data?.message || "Unable to delete the tenant.");
    }
    setLoading(false);
  };

  // Handlers
  const onSearch = (value) => {
    setSearch(value);
    fetchTenants(1, value);
  };

  const onPageChange = (event) => {
    const newPage = event.page + 1;
    fetchTenants(newPage, search);
  };

  const emptyMessage = <span>No tenants found.</span>;

  const columns = [
    {
      header: "Tenant name",
      style: { width: "20%" },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">
          {rowData.name}
        </span>
      ),
    },
    {
      header: "City",
      style: { width: "10%" },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">
          {rowData.city}
        </span>
      ),
    },
    {
      header: "Address",
      style: { width: "20%" },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">
          {rowData.address_1}
        </span>
      ),
    },

    {
      header: "Phone number",
      style: { width: "20%" },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">
          {rowData.phone_number}
        </span>
      ),
    },
    {
      header: "Actions",
      style: { width: "20%" },
      body: (rowData) => (
        <div>
          <Link
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            to={`/tenants/${rowData.id}`}
          >
            View
          </Link>

          <button
            className="ml-2 inline-flex items-center justify-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            onClick={() => deleteTenant(rowData.id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const headerButtons = [
    {
      label: "Create tenant",
      onClick: () => navigate("/tenants/new"),
      className:
        "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500",
    },
  ];

  return {
    columns,
    tenants,
    loading,
    pagination,
    onSearch,
    onPageChange,
    headerButtons,
    emptyMessage,
    fetchTenants,
  };
}
